import './BrandStrength.scss';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import CustomTooltip from '../../../../shared/components/common/tooltip/CustomTooltip';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { TopicsDistribution } from '../../../../shared/models/context-insights/ContextInsightsResponse';
import { KeywordsData } from '../../../../shared/models/tags/TagsResponse';
import { Skeleton } from 'primereact/skeleton';
import EmptyPanel from '../../../../shared/components/empty-panel/EmptyPanel';
import { SuggestedTopic } from '../../../../shared/models/projects/ProjectResponse';
import { Button } from 'primereact/button';

interface BrandStrengthProps {
  topicsDistribution: TopicsDistribution[];
  keyword: string | undefined;
  commonKeywords: KeywordsData[];
  suggestedTopics: SuggestedTopic[];
  isLoading: boolean;
  canExport: boolean;
  isDownloading?: boolean;
  onExportBrandPresence?: () => void | Promise<void>;
}

const BrandStrength: React.FC<BrandStrengthProps> = ({
  topicsDistribution,
  keyword,
  commonKeywords,
  suggestedTopics,
  isLoading,
  canExport,
  isDownloading = false,
  onExportBrandPresence,
}) => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language as 'en' | 'pl';

  const topBrandNames = useMemo(() => {
    const brandTotals = new Map<string, number>();
    topicsDistribution.forEach((topic) => {
      topic.topBrands.forEach(({ keyword: kw, count }) => {
        brandTotals.set(kw, (brandTotals.get(kw) || 0) + count);
      });
    });
    const sorted = Array.from(brandTotals.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([k]) => k);

    // Brand must always be the first column
    if (keyword && !sorted.includes(keyword)) {
      sorted.unshift(keyword);
    } else if (keyword && sorted.includes(keyword)) {
      const idx = sorted.indexOf(keyword);
      sorted.splice(idx, 1);
      sorted.unshift(keyword);
    }

    return sorted.slice(0, 10);
  }, [topicsDistribution, keyword]);

  const nonEmptyTopics = useMemo(
    () => topicsDistribution.filter((t) => t.topBrands.some((b) => b.count > 0)),
    [topicsDistribution]
  );

  const emptyTemplate = () => {
    return (
      <div className="no-results">
        <EmptyPanel />
      </div>
    );
  };

  const handleExportBrandPresence = useCallback(() => {
    if (!canExport || isDownloading || !onExportBrandPresence) {
      return;
    }

    void onExportBrandPresence();
  }, [canExport, isDownloading, onExportBrandPresence]);

  return (
    <div className="stats__panel">
      <div className="page-box__title-action">
        <div className="page-box__title page-box__title--flex">
          {t('contextAnalysis.brandStrengthByTopic')}
          <CustomTooltip tooltipText="contextAnalysis.brandStrengthByTopicTooltip" addMargin={false} />
        </div>
        <div className="page-box__source-filter">
          <Button
            id="brand-strength-export"
            className={`action-btn ${!canExport ? 'btn--disabled-tooltip' : ''}`}
            onClick={handleExportBrandPresence}
          >
            {isDownloading ? (
              <span className="ico--loader ico--xs" />
            ) : (
              <span className={`ico--download ${!canExport ? 'ico--disabled' : ''} ico--xs`} />
            )}
          </Button>
          <CustomTooltip
            tooltipText={!canExport ? 'upgradePlanTrialExportMsg' : 'downloadStats'}
            targetRef="brand-strength-export"
          />
        </div>
      </div>
      {isLoading ? (
        <Skeleton className="skeleton--table skeleton--table-4">
          <span />
          <span />
          <span />
          <span />
        </Skeleton>
      ) : (
        <DataTable
          stripedRows
          paginator={true}
          scrollable
          paginatorClassName="pagination"
          removableSort
          value={nonEmptyTopics}
          rows={10}
          emptyMessage={emptyTemplate}
          rowsPerPageOptions={[10, 25, 50]}
          tableStyle={{ minWidth: '50rem', tableLayout: 'auto' }}
          tableClassName="table"
          className="table-wrapper"
          currentPageReportTemplate={t('rowsNumber') + ':'}
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
        >
          <Column
            header={t('topic')}
            field="topic"
            headerClassName="table__header"
            body={(data) => (
              <div
                style={{ display: 'flex', alignItems: 'center', fontWeight: '600' }}
                className="topic-name"
              >
                {
                  suggestedTopics.find(
                    (st) => st.name.toLowerCase().trim() === data.topic.toLowerCase().trim()
                  )?.[currentLang === 'pl' ? 'namePl' : 'name']
                }
              </div>
            )}
            style={{ minWidth: '120px', maxWidth: '180px' }}
          />
          {topBrandNames.map((brandName) => (
            <Column
              key={brandName}
              header={
                <span style={{ fontWeight: brandName === keyword ? '700' : '400' }}>
                  {commonKeywords.find((keywordData) => keywordData.normalizedKeyword === brandName)
                    ?.keyword || brandName}
                </span>
              }
              headerClassName="table__header table__header--center"
              bodyClassName="table__cell table__cell--center"
              body={(data: TopicsDistribution) => {
                const brand = data.topBrands.find((b) => b.keyword === brandName);
                const isTop = data.topBrandByPercentage?.keyword === brandName;
                return (
                  <span className={`cell-block ${isTop ? 'cell-block--success' : ''}`}>
                    {brand ? `${brand.count}` : '0'}
                  </span>
                );
              }}
            />
          ))}
        </DataTable>
      )}
    </div>
  );
};

export default BrandStrength;
